import { z } from 'zod';

import {
  COUNTERPARTY_BLOCK_MAX_LENGTH,
  COUNTERPARTY_EMAIL_MAX_LENGTH,
  COUNTERPARTY_NAME_MAX_LENGTH,
} from './counterparty.ts';

export const INVOICE_LOAD_ERROR = 'Could not load invoices. Try again or check the logs.';
export const INVOICE_SAVE_ERROR = 'Could not save invoice. Try again or check the logs.';
export const INVOICE_DELETE_ERROR = 'Could not delete invoice. Try again or check the logs.';
export const INVOICE_DUPLICATE_ERROR = 'Could not duplicate invoice. Try again or check the logs.';
export const INVOICE_PAYMENT_STATUS_ERROR =
  'Could not update payment status. Try again or check the logs.';
export const INVOICE_NOT_FOUND_ERROR = 'Invoice not found.';
export const ARCHIVE_INVOICE_READONLY_ERROR = 'Archive invoices are read-only.';
export const INVALID_INVOICE_INPUT_ERROR = 'Check invoice fields and try again.';

export const MAX_INVOICE_NOTES_LENGTH = 10000;
export const MAX_INVOICE_ITEM_DESCRIPTION_LENGTH = 500;
export const MAX_INVOICE_ITEMS = 100;
export const MAX_INVOICE_SEARCH_LENGTH = 200;
export const MAX_INVOICE_REQUEST_BYTES = 256 * 1024;
export const DEFAULT_INVOICE_PAGE_SIZE = 50;
export const MAX_INVOICE_PAGE_SIZE = 100;
export const MAX_INVOICE_QUANTITY_WHOLE_DIGITS = 6;
export const MAX_INVOICE_RATE_WHOLE_DIGITS = 7;
export const MAX_INVOICE_TAX_LABEL_LENGTH = 60;
export const MAX_INVOICE_TAX_RATE_PERCENT = 100;

export const CurrencySchema = z.enum(['EUR', 'USD', 'RUB']);
export type Currency = z.infer<typeof CurrencySchema>;

const IsoDateSchema = z.string().refine(isIsoDate, { message: 'Enter a valid date.' });
const OptionalDueDateSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((value) => {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  })
  .pipe(IsoDateSchema.nullable());

const QuantitySchema = canonicalDecimalSchema(3, MAX_INVOICE_QUANTITY_WHOLE_DIGITS);
const MoneySchema = canonicalDecimalSchema(2, MAX_INVOICE_RATE_WHOLE_DIGITS);
const TaxRateSchema = z
  .string()
  .trim()
  .regex(/^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/)
  .refine((value) => Number.parseFloat(value) <= MAX_INVOICE_TAX_RATE_PERCENT, {
    message: 'Tax rate must be between 0 and 100.',
  });
const OptionalTaxLabelSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((value) => {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  })
  .pipe(z.string().min(1).max(MAX_INVOICE_TAX_LABEL_LENGTH).nullable());

export const InvoiceItemInputSchema = z
  .object({
    description: z.string().trim().min(1).max(MAX_INVOICE_ITEM_DESCRIPTION_LENGTH),
    quantity: QuantitySchema,
    rate: MoneySchema,
  })
  .strict();

export const InvoicePartyInputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }).strict(),
  z.object({ type: z.literal('selectCustomer'), customerId: z.string().trim().min(1) }).strict(),
  z.object({ type: z.literal('keepSnapshot') }).strict(),
  z.object({ type: z.literal('clear') }).strict(),
]);

export const UpsertInvoiceInputSchema = z
  .object({
    date: IsoDateSchema,
    dueDate: OptionalDueDateSchema.default(null),
    currency: CurrencySchema,
    billTo: InvoicePartyInputSchema.default({ type: 'none' }),
    shipTo: InvoicePartyInputSchema.default({ type: 'none' }),
    notes: z.string().max(MAX_INVOICE_NOTES_LENGTH).default(''),
    items: z.array(InvoiceItemInputSchema).min(1).max(MAX_INVOICE_ITEMS),
    taxRate: TaxRateSchema.default('0'),
    taxLabel: OptionalTaxLabelSchema.default(null),
  })
  .strict()
  .refine((value) => value.dueDate === null || value.dueDate >= value.date, {
    path: ['dueDate'],
    message: 'Due date must be on or after Date.',
  });

const PageSchema = z.preprocess(
  (value) => coercePositiveInteger(value),
  z.number().int().min(1).default(1),
);
const PageSizeSchema = z.preprocess(
  (value) => coercePositiveInteger(value),
  z.number().int().min(1).max(MAX_INVOICE_PAGE_SIZE).default(DEFAULT_INVOICE_PAGE_SIZE),
);
const YearSchema = z.preprocess((value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return coercePositiveInteger(value);
}, z.number().int().min(1900).max(9999).optional());

export const InvoiceListQuerySchema = z
  .object({
    currency: CurrencySchema.optional(),
    year: YearSchema,
    page: PageSchema,
    pageSize: PageSizeSchema,
  })
  .strict();

export const InvoiceSearchInputSchema = z
  .object({
    q: z.string().max(MAX_INVOICE_SEARCH_LENGTH).default(''),
    currency: CurrencySchema.optional(),
    year: YearSchema,
    page: PageSchema,
    pageSize: PageSizeSchema,
  })
  .strict();

export const DuplicateInvoiceInputSchema = z
  .object({
    date: IsoDateSchema,
  })
  .strict();

export const InvoicePaymentStatusInputSchema = z.object({ isPaid: z.boolean() }).strict();

export const InvoiceSnapshotSchema = z
  .object({
    name: z.string().min(1).max(COUNTERPARTY_NAME_MAX_LENGTH),
    email: z.string().email().max(COUNTERPARTY_EMAIL_MAX_LENGTH).nullable(),
    billTo: z.string().max(COUNTERPARTY_BLOCK_MAX_LENGTH).nullable(),
    shipTo: z.string().max(COUNTERPARTY_BLOCK_MAX_LENGTH).nullable(),
  })
  .strict();

export const InvoiceSenderProfileSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    signaturePreviewUrl: z
      .string()
      .regex(/^\/api\/sender-profiles\/[^/?#]+\/signature\?v=\d+$/)
      .nullable(),
    bankRequisites: z.string(),
  })
  .strict();

export const InvoiceItemSchema = z
  .object({
    id: z.string().min(1),
    description: z.string().min(1).max(MAX_INVOICE_ITEM_DESCRIPTION_LENGTH),
    quantity: QuantitySchema,
    rate: MoneySchema,
    position: z.number().int().nonnegative(),
    amountCents: z.number().int().nonnegative(),
  })
  .strict();

export const InvoiceDetailSchema = z
  .object({
    id: z.string().min(1),
    number: z.number().int().min(1),
    date: IsoDateSchema,
    dueDate: IsoDateSchema.nullable(),
    currency: CurrencySchema,
    senderProfile: InvoiceSenderProfileSchema,
    billToId: z.string().min(1).nullable(),
    shipToId: z.string().min(1).nullable(),
    billToSnapshot: InvoiceSnapshotSchema.nullable(),
    shipToSnapshot: InvoiceSnapshotSchema.nullable(),
    notes: z.string().max(MAX_INVOICE_NOTES_LENGTH),
    isArchive: z.boolean(),
    isPaid: z.boolean(),
    items: z.array(InvoiceItemSchema).min(1),
    taxRate: TaxRateSchema,
    taxLabel: z.string().min(1).max(MAX_INVOICE_TAX_LABEL_LENGTH).nullable(),
    subtotalCents: z.number().int().nonnegative(),
    taxCents: z.number().int().nonnegative(),
    totalCents: z.number().int().nonnegative(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict();

export const InvoiceListItemSchema = z
  .object({
    id: z.string().min(1),
    number: z.number().int().min(1),
    date: IsoDateSchema,
    dueDate: IsoDateSchema.nullable(),
    currency: CurrencySchema,
    billToName: z.string().min(1).max(COUNTERPARTY_NAME_MAX_LENGTH).nullable(),
    shipToName: z.string().min(1).max(COUNTERPARTY_NAME_MAX_LENGTH).nullable(),
    totalCents: z.number().int().nonnegative(),
    isArchive: z.boolean(),
    isPaid: z.boolean(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict();

export const InvoiceCurrencyTotalsSchema = z
  .object({
    currency: CurrencySchema,
    invoicedCents: z.number().int().nonnegative(),
    paidCents: z.number().int().nonnegative(),
  })
  .strict();

export const InvoiceListTotalsSchema = z
  .object({
    byCurrency: z.array(InvoiceCurrencyTotalsSchema),
  })
  .strict();

const PaginationSchema = z
  .object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(MAX_INVOICE_PAGE_SIZE),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  })
  .strict();

export const InvoiceListResponseSchema = z
  .object({
    invoices: z.array(InvoiceListItemSchema),
    pagination: PaginationSchema,
    totals: InvoiceListTotalsSchema,
  })
  .strict();

export const InvoiceDetailResponseSchema = z.object({ invoice: InvoiceDetailSchema }).strict();
export const InvoiceResponseSchema = InvoiceDetailResponseSchema;
export const DeleteInvoiceResponseSchema = z.object({ ok: z.literal(true) }).strict();

export type InvoiceItemInput = z.infer<typeof InvoiceItemInputSchema>;
export type InvoicePartyInput = z.infer<typeof InvoicePartyInputSchema>;
export type UpsertInvoiceInput = z.infer<typeof UpsertInvoiceInputSchema>;
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;
export type InvoiceSearchInput = z.infer<typeof InvoiceSearchInputSchema>;
export type DuplicateInvoiceInput = z.infer<typeof DuplicateInvoiceInputSchema>;
export type InvoicePaymentStatusInput = z.infer<typeof InvoicePaymentStatusInputSchema>;
export type InvoiceSnapshot = z.infer<typeof InvoiceSnapshotSchema>;
export type InvoiceItem = z.infer<typeof InvoiceItemSchema>;
export type InvoiceDetail = z.infer<typeof InvoiceDetailSchema>;
export type InvoiceListItem = z.infer<typeof InvoiceListItemSchema>;
export type InvoiceCurrencyTotals = z.infer<typeof InvoiceCurrencyTotalsSchema>;
export type InvoiceListTotals = z.infer<typeof InvoiceListTotalsSchema>;
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;
export type InvoiceDetailResponse = z.infer<typeof InvoiceDetailResponseSchema>;
export type InvoiceResponse = InvoiceDetailResponse;
export type DeleteInvoiceResponse = z.infer<typeof DeleteInvoiceResponseSchema>;

function canonicalDecimalSchema(maxScale: number, maxWholeDigits: number) {
  return z
    .string()
    .trim()
    .regex(new RegExp(`^(?:0|[1-9]\\d{0,${maxWholeDigits - 1}})(?:\\.\\d{1,${maxScale}})?$`))
    .refine((value) => !isZeroDecimal(value));
}

function isZeroDecimal(value: string): boolean {
  return /^0(?:\.0+)?$/.test(value);
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day
  );
}

function coercePositiveInteger(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return Number.NaN;
}
